import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { McIcon } from '@maersk-global/mds-react-wrapper/components-core/mc-icon';

const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

const INCIDENT_LEVELS = [
  { key: 'p1', label: 'P1', color: '#b91c1c' },
  { key: 'p2', label: 'P2', color: '#c2410c' },
  { key: 'p3', label: 'P3', color: '#92400e' },
  { key: 'p4', label: 'P4', color: '#6b7280' }
];

const IncidentsSummaryCard = ({
  title = 'INCIDENTS DUE TO CHANGES',
  subtitle = 'Past 30 days',
  incidentsTotal = 0,
  incidentsBreakdown = {},
  isLoading = false,
  onCardClick,
  isActive = false
}) => {
  const isClickable = typeof onCardClick === 'function' && incidentsTotal > 0;

  const handleCardActivation = (event) => {
    if (!isClickable) return;
    if (event?.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    if (event?.type === 'keydown') {
      event.preventDefault();
    }
    onCardClick();
  };

  const renderBreakdown = () => (
    <Typography
      sx={{
        fontSize: '1rem',
        color: '#4b5563',
        fontWeight: 500,
        fontFamily: maerskFontFamily,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5
      }}
    >
      {INCIDENT_LEVELS.map((level, index) => (
        <React.Fragment key={level.key}>
          <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography component="span" sx={{ fontSize: '1.05rem', fontWeight: 700, color: level.color, fontFamily: maerskFontFamily }}>
              {incidentsBreakdown[level.key] || 0}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.95rem', color: '#4b5563', fontFamily: maerskFontFamily }}>
              {level.label}
            </Typography>
          </Box>
          {index < INCIDENT_LEVELS.length - 1 && (
            <Typography component="span" sx={{ color: '#9ca3af', fontFamily: maerskFontFamily }}>/</Typography>
          )}
        </React.Fragment>
      ))}
    </Typography>
  );

  const renderContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, textAlign: 'left', fontFamily: maerskFontFamily, flexGrow: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: '#fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <McIcon icon="exclamation-triangle" size="24" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.08em', color: '#4b5563', fontFamily: maerskFontFamily }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: maerskFontFamily }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ fontSize: '34px', fontWeight: 600, color: '#111827', lineHeight: 1, fontFamily: maerskFontFamily }}>
        {incidentsTotal}
      </Typography>
      {renderBreakdown()}
    </Box>
  );

  return (
    <Box
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={handleCardActivation}
      onKeyDown={handleCardActivation}
      sx={{
        width: '100%',
        backgroundColor: '#fff',
        borderRadius: 2,
        padding: '20px',
        border: isActive ? '2px solid #42b0d5' : '1px solid #9ca3af',
        minHeight: 180,
        fontFamily: maerskFontFamily,
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        opacity: isClickable ? 1 : 0.7,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderContent()
      )}
    </Box>
  );
};

export default IncidentsSummaryCard;
